import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Textarea,
  FormControl,
  FormLabel,
  Box,
  Badge,
  Divider,
  useToast,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import axios from "axios";

const OfferApprovalModal = ({ isOpen, onClose, offer, onApprovalComplete }) => {
  const [comments, setComments] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const toast = useToast();

  const handleApprove = async () => {
    setActionType("approve");
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/offers/${offer._id}/approve`,
        { comments },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast({
        title: "Offer Approved",
        description:
          response.data.message || "Offer has been approved successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onApprovalComplete();
      onClose();
    } catch (error) {
      toast({
        title: "Approval Failed",
        description: error.response?.data?.message || "Failed to approve offer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setActionType(null);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast({
        title: "Rejection Reason Required",
        description: "Please provide a reason for rejection",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setActionType("reject");
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/offers/${offer._id}/reject`,
        { rejectionReason, comments },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast({
        title: "Offer Rejected",
        description: response.data.message || "Offer has been rejected",
        status: "info",
        duration: 5000,
        isClosable: true,
      });

      onApprovalComplete();
      onClose();
    } catch (error) {
      toast({
        title: "Rejection Failed",
        description: error.response?.data?.message || "Failed to reject offer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setActionType(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Offer Approval - {offer?.offerId}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Offer Information */}
            <Box>
              <Text fontWeight="bold" fontSize="lg" mb={2}>
                Offer Details
              </Text>
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text color="gray.600">Candidate:</Text>
                  <Text fontWeight="semibold">
                    {offer?.candidateId?.fullName || "N/A"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Position:</Text>
                  <Text fontWeight="semibold">
                    {offer?.positionTitle || "N/A"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">CTC:</Text>
                  <Text fontWeight="semibold" color="green.600">
                    ₹{offer?.ctc?.toLocaleString() || "N/A"}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Current Status:</Text>
                  <Badge
                    colorScheme={
                      offer?.approvalStatus === "pending" ? "yellow" : "blue"
                    }
                  >
                    {offer?.approvalStatus || "N/A"}
                  </Badge>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* Approval Workflow Info */}
            {offer?.approvalId && (
              <Box>
                <Text fontWeight="bold" fontSize="lg" mb={2}>
                  Approval Workflow
                </Text>
                <HStack justify="space-between">
                  <Text color="gray.600">Workflow Type:</Text>
                  <Badge colorScheme="purple">
                    {offer.approvalWorkflowType || "sequential"}
                  </Badge>
                </HStack>
                <HStack justify="space-between" mt={2}>
                  <Text color="gray.600">Current Stage:</Text>
                  <Text fontWeight="semibold">
                    Stage {offer.currentApprovalStage || 1}
                  </Text>
                </HStack>
              </Box>
            )}

            <Divider />

            {/* Comments Section */}
            <FormControl>
              <FormLabel>Comments (Optional)</FormLabel>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments or notes..."
                rows={3}
              />
            </FormControl>

            {/* Rejection Reason Section */}
            <FormControl>
              <FormLabel>
                Rejection Reason{" "}
                {actionType === "reject" && (
                  <Text as="span" color="red.500">
                    *
                  </Text>
                )}
              </FormLabel>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Required if rejecting the offer..."
                rows={3}
                borderColor={
                  actionType === "reject" && !rejectionReason
                    ? "red.300"
                    : "gray.200"
                }
              />
            </FormControl>

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontSize="sm">
                  Your decision will be recorded with timestamp and user
                  details.
                  {offer?.approvalWorkflowType === "sequential" &&
                    " The offer will move to the next approval stage upon approval."}
                </Text>
              </Box>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleReject}
              isLoading={isSubmitting && actionType === "reject"}
              isDisabled={isSubmitting}
            >
              Reject
            </Button>
            <Button
              colorScheme="green"
              onClick={handleApprove}
              isLoading={isSubmitting && actionType === "approve"}
              isDisabled={isSubmitting}
            >
              Approve
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OfferApprovalModal;
